import { cn } from "@/lib/utils/index";
import { forwardRef, type ReactNode } from "react";
import { FetchTooltip } from "./FetchTooltip";

/** Status and tag colors. Each is a dark text on a light tint of the same hue, AA or better. */
export type FetchPillTone = "neutral" | "indigo" | "success" | "warning" | "danger" | "info";

interface FetchPillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "error";
  selected?: boolean;
  /** Custom hex color (e.g., "#6366f1"). When provided and selected, renders a tinted pill with this color. */
  color?: string;
  /** Size variant. "md" (default) for filter tabs, "sm" for inline labels/tags. */
  size?: "sm" | "md";
  /**
   * Element to render. "button" (default) for interactive, "span" for display-only labels. With a
   * `tone`, defaults to "button" when `onClick` is set and "span" otherwise.
   */
  as?: "button" | "span";
  /** When true and `color` is provided, uses solid color background with white text instead of tinted. */
  solid?: boolean;
  /**
   * Status or tag look: one of the shared tones, medium weight, a fixed height per size (sm 20px,
   * md 24px) and no outline. When set, `variant`, `color`, `solid` and `selected` no longer style the
   * pill (`selected` still reports `aria-pressed`). Omit it to keep the filter-pill look.
   */
  tone?: FetchPillTone;
  /** Leading icon (a Lucide element, sized for you) or a small avatar. Only used with `tone`. */
  leading?: ReactNode;
  /**
   * Shown on hover and keyboard focus, for example "Changed on Oct 12". On a display-only pill a
   * string is also added as screen-reader text, since a span cannot take focus. Only used with `tone`.
   */
  tooltip?: ReactNode;
}

/** Size-specific classes */
const sizeStyles = {
  sm: "px-2 py-0.5 text-sm font-bold",
  md: "px-3 py-1.5 text-sm font-semibold",
};

/** Tag sizes: a fixed height so a row of tags never jumps, padding tightened beside a leading slot. */
const TONE_SIZE_STYLES: Record<"sm" | "md", { base: string; withLeading: string; leading: string }> = {
  sm: { base: "h-5 gap-1 px-2", withLeading: "pl-1", leading: "[&>svg]:size-3" },
  md: { base: "h-6 gap-1.5 px-2.5", withLeading: "pl-1.5", leading: "[&>svg]:size-3.5" },
};

/** Tone colors at rest, and the one-step-darker tint an interactive pill takes on hover. */
const TONE_STYLES: Record<FetchPillTone, { rest: string; hover: string }> = {
  neutral: { rest: "bg-gray-100 text-gray-700", hover: "hover:bg-gray-200" },
  indigo: { rest: "bg-indigo-50 text-indigo-700", hover: "hover:bg-indigo-100" },
  success: { rest: "bg-emerald-50 text-emerald-700", hover: "hover:bg-emerald-100" },
  warning: { rest: "bg-amber-50 text-amber-700", hover: "hover:bg-amber-100" },
  danger: { rest: "bg-red-50 text-red-700", hover: "hover:bg-red-100" },
  info: { rest: "bg-sky-50 text-sky-700", hover: "hover:bg-sky-100" },
};

const TONE_BASE_CLASS =
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-full text-xs font-medium leading-none transition-colors";

const TONE_INTERACTIVE_CLASS =
  "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Nowadays themed pill/tag component.
 * Supports fixed variants (default, error) and custom hex colors for dynamic theming.
 * Use `as="span"` + `size="sm"` for display-only label badges.
 *
 * Pass a `tone` for a status or tag: the shared tone colors, an optional leading icon or avatar,
 * and an optional tooltip. A toned pill with `onClick` renders as a button with a hover tint.
 *
 * @param props - See {@link FetchPillProps}.
 * @returns The pill, wrapped in a tooltip when a toned pill has one.
 */
export const FetchPill = forwardRef<HTMLButtonElement, FetchPillProps>(
  (
    {
      className,
      variant = "default",
      selected,
      color,
      size = "md",
      as,
      solid = false,
      tone,
      leading,
      tooltip,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const Element = as ?? (tone && !props.onClick ? "span" : "button");
    const isSelected = selected ?? false;
    const baseStyles = cn("inline-flex items-center rounded-full transition-colors", sizeStyles[size]);
    // Toggle semantics: only announce aria-pressed when the caller actually passed a `selected`
    // state. Pills used as plain action buttons (e.g. "click to copy" variable chips) aren't
    // toggles and shouldn't be announced as one just because they render as a <button>. Spans
    // (display-only badges) skip this regardless.
    const pressedAttr = Element === "button" && selected !== undefined ? { "aria-pressed": isSelected } : {};

    // Tone mode: status and tag look
    if (tone) {
      const sizing = TONE_SIZE_STYLES[size];
      const isInteractive = Element === "button";
      const pill = (
        <Element
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cn(
            TONE_BASE_CLASS,
            sizing.base,
            leading ? sizing.withLeading : "",
            TONE_STYLES[tone].rest,
            isInteractive ? cn(TONE_INTERACTIVE_CLASS, TONE_STYLES[tone].hover) : "",
            className
          )}
          style={style}
          {...props}
          {...(isInteractive ? { type: "button" as const } : {})}
          {...pressedAttr}
        >
          {leading ? (
            <span aria-hidden="true" className={cn("flex shrink-0 items-center", sizing.leading)}>
              {leading}
            </span>
          ) : null}
          {children}
          {!isInteractive && typeof tooltip === "string" ? <span className="sr-only">. {tooltip}</span> : null}
        </Element>
      );
      return tooltip ? <FetchTooltip content={tooltip}>{pill}</FetchTooltip> : pill;
    }

    // Custom color mode: solid, tinted (selected), or gray (unselected)
    if (color) {
      const colorStyles = solid
        ? { backgroundColor: color, color: "#fff" }
        : isSelected
          ? { backgroundColor: `${color}30`, color }
          : undefined;
      return (
        <Element
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cn(baseStyles, isSelected ? "" : "bg-gray-100 text-gray-700 hover:bg-gray-200", className)}
          style={{ ...colorStyles, ...style }}
          {...props}
          {...(Element === "button" ? { type: "button" } : {})}
          {...pressedAttr}
        >
          {children}
        </Element>
      );
    }

    // Standard variant mode
    const variants = {
      default: isSelected
        ? "bg-indigo-100 text-indigo-700 ring-1 ring-inset ring-indigo-600 hover:bg-indigo-200"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200",
      error: "bg-red-100 text-red-700 hover:bg-red-200",
    };

    return (
      <Element
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn(baseStyles, variants[variant], className)}
        style={style}
        {...props}
        {...(Element === "button" ? { type: "button" } : {})}
        {...pressedAttr}
      >
        {children}
      </Element>
    );
  }
);

FetchPill.displayName = "FetchPill";
